import { ArrowLeft, ArrowRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

type PageToken = number | 'ellipsis-start' | 'ellipsis-end';

/** Which page buttons/ellipses to show for a given page/total, independent of rendering. */
function getPageTokens(currentPage: number, totalPages: number): PageToken[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  let startPage: number;
  let endPage: number;
  if (currentPage <= 3) {
    // Near the beginning: show 1, 2, 3, 4, ..., last
    startPage = 1;
    endPage = 3;
  } else if (currentPage >= totalPages - 4) {
    // Near the end: show 1, ..., last-3, last-2, last-1, last
    startPage = totalPages - 4;
    endPage = totalPages - 2;
  } else {
    // In the middle: show 1, ..., current-1, current, current+1, ..., last
    startPage = currentPage - 1;
    endPage = currentPage + 1;
  }

  const tokens: PageToken[] = [0];
  if (startPage > 1) tokens.push('ellipsis-start');

  for (let i = startPage; i <= endPage; i++) {
    if (i > 0 && i < totalPages - 1) tokens.push(i);
  }

  if (endPage < totalPages - 2) tokens.push('ellipsis-end');
  if (totalPages > 1) tokens.push(totalPages - 1);

  return tokens;
}

export default function TablePagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}: Readonly<PaginationProps>) {
  const createPageButton = (pageIndex: number) => (
    <button
      key={pageIndex}
      onClick={() => onPageChange(pageIndex)}
      disabled={disabled}
      className={`px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
        currentPage === pageIndex ? 'bg-active text-slate-600' : 'bg-transparent text-slate-400 hover:text-slate-600'
      }`}
    >
      {pageIndex + 1}
    </button>
  );

  const createEllipsis = (key: string) => (
    <span key={key} className="px-3 py-1 text-slate-400">
      ...
    </span>
  );

  const renderPageNumbers = () =>
    getPageTokens(currentPage, totalPages).map(token =>
      typeof token === 'number' ? createPageButton(token) : createEllipsis(token),
    );

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="mt-4 p-4">
      <div className="flex justify-between">
        <button
          aria-disabled={disabled || currentPage === 0}
          onClick={() => {
            if (!(disabled || currentPage === 0)) onPageChange(currentPage - 1);
          }}
          className={`flex flex-row rounded-lg items-center align-middle border dark:border-gray-600 py-2 px-[15px] text-slate-600 dark:text-gray-300 text-sm
    ${
      disabled || currentPage === 0
        ? 'opacity-50 cursor-not-allowed'
        : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-700'
    }`}
        >
          <ArrowLeft size={14} className="mr-1" />
          Previous
        </button>

        <div className="hidden md:flex items-center gap-1 text-sm">{renderPageNumbers()}</div>

        <button
          aria-disabled={disabled || currentPage === totalPages - 1}
          onClick={() => {
            if (!(disabled || currentPage === totalPages - 1)) onPageChange(currentPage + 1);
          }}
          className={`flex flex-row rounded-lg items-center align-middle border dark:border-gray-600 py-2 px-[15px] text-slate-600 dark:text-gray-300 text-sm
    ${
      disabled || currentPage === totalPages - 1
        ? 'opacity-50 cursor-not-allowed'
        : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-700'
    }`}
        >
          Next
          <ArrowRight size={14} className="ml-1" />
        </button>
      </div>
    </div>
  );
}
